/**
 * Validates the delay configuration of the first block in an array.
 * A delay is considered valid if:
 * - No delay field exists (delay is optional), OR
 * - Delay field exists and at least one of its numeric properties is not zero
 *
 * @param {Array} currentBlock - Array of block objects, only the first element is checked
 * @returns {boolean} Returns true if delay is valid (missing or has non-zero values),
 *                    false if delay exists but all values are zero
 *
 * @example
 * // Valid: no delay field (optional)
 * const blocks = [{ name: 'action', type: 'email' }];
 * isDelayValid(blocks); // true
 *
 * @example
 * // Valid: delay with at least one non-zero value
 * const blocks = [{ delay: { hours: 0, minutes: 5, seconds: 0 } }];
 * isDelayValid(blocks); // true
 *
 * @example
 * // Invalid: delay with all zero values
 * const blocks = [{ delay: { hours: 0, minutes: 0, seconds: 0 } }];
 * isDelayValid(blocks); // false
 *
 * @example
 * // Valid: delay with single non-zero value
 * const blocks = [{ delay: { timeout: 100 } }];
 * isDelayValid(blocks); // true
 *
 * @example
 * // Invalid: empty delay object (no properties, so all are zero)
 * const blocks = [{ delay: {} }];
 * isDelayValid(blocks); // false
 *
 * @example
 * // Edge case: empty array
 * isDelayValid([]); // false
 */
export const isDelayValid = (currentBlock) => {
    // Handle edge cases
    if (!currentBlock || currentBlock.length === 0) {
        return false;
    }

    const [block] = currentBlock;

    // Handle case where block is null/undefined
    if (!block) {
        return false;
    }

    // If no delay field exists, it's valid (optional field)
    if (!block.delay) {
        return true;
    }

    // If delay exists, check if at least one value is not zero
    const delayValues = Object.values(block.delay);

    // If delay object is empty, consider it invalid (no actual delay configured)
    if (delayValues.length === 0) {
        return false;
    }

    // Valid if at least one value is not zero
    return delayValues.some((value) => value !== 0);
};
